import L from 'leaflet'
import moment from 'moment'
import { requireEnv } from './utils'

const ECMC_BASE = 'https://ecmc.state.co.us/cogisdb'
const CARTO_API_KEY = requireEnv('VITE_CARTO_API_KEY')

// const ONE_MILE_M = 1609.34
// const TWO_THOUSAND_FT_M = 609.6

export const BASEMAPS: Record<string, L.TileLayer> = {
  'CartoDB Positron': L.tileLayer(`https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=${CARTO_API_KEY}`, {
    maxZoom: 19,
    subdomains: 'abcd',
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, &copy; <a href="https://carto.com/attributions" target="_blank">CARTO</a>',
  }),
  'OpenStreetMap': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    maxZoom: 19, opacity: 0.7,
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
  }),
  'Stadia Maps': L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png', {
    maxZoom: 20,
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, &copy; <a href="https://stadiamaps.com/" target="_blank">Stadia Maps</a>, &copy; <a href="https://openmaptiles.org/" target="_blank">OpenMapTiles</a>, &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a>',
  }),
  'Esri World Imagery': L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
    maxZoom: 19, opacity: 0.7,
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, Tiles &copy; Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
  }),
  'Google Satellite': L.tileLayer('https://www.google.cn/maps/vt?lyrs=s@189&gl=cn&x={x}&y={y}&z={z}', {
    maxZoom: 20, opacity: 0.7,
    attribution: '&copy; <a href="https://ecocarto.com/" target="_blank">EcoCarto</a>, &copy; <a href="https://www.google.com/maps" target="_blank">Google Maps</a>',
  }),
}

export const Colors = {
  fallback:    '#FF0000',
  well_lp:     '#ff9900',
  well_pa:     '#999999',
  well_active: '#cc0000',
}

export interface LegendItem {
  label: string
  type?: 'circle' | 'line' | 'polygon'
  color?: string
  fillColor?: string
  weight?: number
  rotation?: number
  dashArray?: string
  svg?: string
}

type LegendPosition = 'bottomleft' | 'bottomright' | 'topleft' | 'topright'

export function buildLocationsLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    pointToLayer: (feature, latlng) => createLocationMarker(latlng, feature.properties ?? {}),
    onEachFeature: (feature, layer) => {
      layer.bindTooltip(buildLocationTooltip(feature.properties as Record<string, any>), { sticky: true, opacity: 0.9 })
    },
  })
}

function buildLocationTooltip(p: Record<string, any>): string {
  const active = p.wells - (p.wells_pa ?? 0)
  return [
    `<b>${p.location_name}</b>`,
    p.operator ?? '',
    `Wells: ${p.wells} (${active} active, ${p.wells_pa ?? 0} plugged)`,
    p.wells_lp ? `Low Producing: ${p.wells_lp}` : '',
    p.spills ? `Spills: ${p.spills}` : '',
    p.complaints ? `Complaints: ${p.complaints}` : '',
    p.first_spud ? `Spud: ${formatDate(p.first_spud, "MMM 'YY")}–${formatDate(p.last_spud, "MMM 'YY")}` : '',
    p.first_plugged ? `Plugged: ${formatDate(p.first_plugged, "MMM 'YY")}–${formatDate(p.last_plugged, "MMM 'YY")}` : '',
    p.frac_water_mm_gal ? `Frac Water: ${p.frac_water_mm_gal.toLocaleString()}M gal` : '',
    p.frac_sand_tons ? `Frac Sand: ${p.frac_sand_tons.toLocaleString()} tons` : '',
    p.distance_feet ? `Distance to River: ${Number(p.distance_feet).toLocaleString()} ft` : '',
  ].filter(Boolean).join('<br>')
}

export function buildRiverLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    style: {
      color: '#1f78b4',
      weight: 2,
      opacity: 0.9,
    },
  })
}

export function buildSpillsLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    pointToLayer: (feature, latlng) => createSpillMarker(latlng, feature.properties ?? {}),
    onEachFeature: (feature, layer) => {
      const tooltip = buildSpillTooltip(feature.properties as Record<string, any>)
      const group = layer as L.LayerGroup
      const layers = group.getLayers()
      const hitTarget = layers[layers.length - 1]
      hitTarget.bindTooltip(tooltip, { sticky: true, opacity: 0.9 })
    },
  })
}

function buildSpillTooltip(p: Record<string, any>): string {
  return [
    p.spill_type == 'Historical' ? '<b>Historical Release</b>' : '<b>Recent Spill</b>',
    p.operator ?? '',
    p.facility_type ? p.facility_type : '',
    p.api_number ? `API: ${formatIdentifier('apiNumber', p.api_number)}` : '',
    p.date_of_discovery ? `Discovered: ${formatDate(p.date_of_discovery)}` : '',
    p.initial_report_date ? `Reported: ${formatDate(p.initial_report_date)}` : '',
    p.distance_feet ? `Distance to River: ${Math.round(p.distance_feet).toLocaleString()} feet` : '',
  ].filter(Boolean).join('<br>')
}

export function buildTanksLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    pointToLayer: (feature, latlng) => createTankMarker(latlng, feature.properties ?? {}),
    onEachFeature: (feature, layer) => {
      const tooltip = buildTankTooltip(feature.properties as Record<string, any>)
      const group = layer as L.LayerGroup
      const layers = group.getLayers()
      const hitTarget = layers[layers.length - 1]
      hitTarget.bindTooltip(tooltip, { sticky: true, opacity: 0.9 })
    },
  })
}

function buildTankTooltip(p: Record<string, any>): string {
  return [
    `<b>${p.name || 'Tank'} (${p.facility_status})</b>`,
    p.operator ?? '',
    p.distance_feet ? `Distance to River: ${Math.round(p.distance_feet).toLocaleString()} feet` : '',
  ].filter(Boolean).join('<br>')
}

export function buildTrailLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    style: {
      color: '#734c00',
      weight: 2,
      opacity: 0.9,
      dashArray: '6, 6',
    },
  })
}

export function buildWellsLayer(geojson: GeoJSON.FeatureCollection): L.GeoJSON {
  return L.geoJSON(geojson, {
    pointToLayer: (feature, latlng) => createWellMarker(latlng, feature.properties ?? {}),
    onEachFeature: (feature, layer) => {
      const tooltip = buildWellTooltip(feature.properties as Record<string, any>)
      const group = layer as L.LayerGroup
      const layers = group.getLayers()
      const hitTarget = layers[layers.length - 1]
      hitTarget.bindTooltip(tooltip, { sticky: true, opacity: 0.9 })
    },
  })
}

function buildWellTooltip(p: Record<string, any>): string {
  return [
    `<b>${p.well_title || p.well_name || 'Well'} (${p.facility_status})</b>`,
    p.operator ?? '',
    p.api_number ? `API: ${formatIdentifier('apiNumber', p.api_number)}` : '',
    p.spud_date ? `Spud: ${formatDate(p.spud_date)}` : '',
    p.facility_status === 'PA' && p.status_date ? `Plugged: ${formatDate(p.status_date)}` : '',
    p.distance_feet ? `Distance to River: ${Math.round(p.distance_feet).toLocaleString()} feet` : '',
    p.lp_designation ? `<span style="color:#f90">${p.lp_designation}</span>` : '',
  ].filter(Boolean).join('<br>')
}

export function createLegend(items: LegendItem[], position: LegendPosition = 'bottomleft'): L.Control {
  const control = L.Control.extend({
    options: { position },
    onAdd() {
      const container = L.DomUtil.create('div', 'leaflet-legend')
      container.style.cssText = 'background:#fff;border-radius:4px;border:1px solid #ccc;padding:3px 5px 3px 8px;font-size:12px;line-height:1.25'
      L.DomEvent.disableClickPropagation(container)

      container.innerHTML = items.map(item => {
        const icon = item.svg
          ? `<img src="${item.svg}" style="width:12px;height:12px;margin-right:8px;vertical-align:middle;display:inline-block">`
          : (() => {
              const isLine = item.type === 'line'
              const dash = item.dashArray
                ? `background:transparent;outline:${item.weight ?? 1}px ${item.color} dashed;outline-offset:-${item.weight ?? 1}px;`
                : isLine
                  ? `background-color:${item.color};`
                  : `background-color:${item.fillColor ?? item.color};border:${item.weight ?? 1}px solid ${item.color};`
              const shape = item.type === 'circle'
                ? 'border-radius:50%;'
                : isLine
                  ? ''
                  : item.rotation ? `transform:rotate(${item.rotation}deg);` : ''
              const size = isLine ? `width:16px;height:${item.weight ?? 2}px;` : 'width:8px;height:8px;'
              return `<span style="display:inline-block;${size}${dash}${shape}margin-right:8px;vertical-align:middle;"></span>`
            })()
        return `<div style="margin-bottom:6px;display:flex;align-items:center">${icon}<span style="font-size:12px;vertical-align:middle">${item.label}</span></div>`
      }).join('')

      return container
    },
  })
  return new control()
}

export function createLocationMarker(latlng: L.LatLngExpression, props: Record<string, any>): L.CircleMarker {
  const radius = flanneryRadius(props.wells ?? 0)
  const marker = L.circleMarker(latlng, {
    radius,
    color: '#12aa4e',
    fillColor: '#12aa4e',
    fillOpacity: 0.25,
    weight: 2,
    dashArray: '4, 4',
  })

  marker.on('dblclick', (e: L.LeafletMouseEvent) => {
    const facId = props.location_id as string | undefined
    if (facId) {
      window.open(`${ECMC_BASE}/Facility/FacilityDetail?facid=${facId}`, '_blank')
      L.DomEvent.stopPropagation(e)
    }
  })

  return marker
}

export function createSpillMarker(latlng: L.LatLngExpression, props: Record<string, any>): L.LayerGroup {
  const marker = L.marker(latlng, { icon: L.divIcon({
    className: 'custom-diamond-icon',
    html: '<div style="width:8px;height:8px;background-color:rgba(255,105,180,0.2);transform:rotate(45deg);border:1px solid #ff1493;pointer-events:none"></div>',
    iconSize: [8, 8], iconAnchor: [4, 4],
  })})
  const hitTarget = L.circleMarker(latlng, { radius: 12, color: 'transparent', fillColor: 'transparent', fillOpacity: 0, weight: 0, interactive: true })

  const openLink = (e: L.LeafletMouseEvent) => {
    const doc = props.document_number as string | undefined
    if (doc) {
      window.open(`${ECMC_BASE}/Incidents/reportDetail?report=spill&doc_num=${doc}`, '_blank')
      L.DomEvent.stopPropagation(e)
    }
  }

  marker.on('dblclick', openLink)
  hitTarget.on('dblclick', openLink)

  return L.layerGroup([hitTarget, marker])
}

export function createTankMarker(latlng: L.LatLngExpression, props: Record<string, any>): L.LayerGroup {
  const marker = L.marker(latlng, { icon: L.icon({
    iconUrl: `${import.meta.env.BASE_URL}/tank.svg`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  })})
  const hitTarget = L.circleMarker(latlng, { radius: 20, color: 'transparent', fillColor: 'transparent', fillOpacity: 0, weight: 0, interactive: true })

  const openLink = (e: L.LeafletMouseEvent) => {
    const facId = props.facility_id as string | undefined
    if (facId) {
      window.open(`${ECMC_BASE}/Facility/FacilityDetail?facid=${facId}`, '_blank')
      L.DomEvent.stopPropagation(e)
    }
  }

  marker.on('dblclick', openLink)
  hitTarget.on('dblclick', openLink)

  return L.layerGroup([hitTarget, marker])
}

export function createWellMarker(latlng: L.LatLngExpression, props: Record<string, any>): L.LayerGroup {
  const isLP = !!props.lp_designation
  const color = isLP ? Colors.well_lp : props.facility_status === 'PA' ? Colors.well_pa : Colors.well_active
  const marker = L.circleMarker(latlng, { radius: 4, color, fillColor: color, fillOpacity: 0.4, weight: 1 })
  const hitTarget = L.circleMarker(latlng, { radius: 12, color: 'transparent', fillColor: 'transparent', fillOpacity: 0, weight: 0, interactive: true })

  const openLink = (e: L.LeafletMouseEvent) => {
    const api = props.api_number as string | undefined
    if (api) {
      window.open(`${ECMC_BASE}/Facility/FacilityDetail?api=${api.startsWith('05') ? api.slice(2) : api}`, '_blank')
      L.DomEvent.stopPropagation(e)
    }
  }

  marker.on('dblclick', openLink)
  hitTarget.on('dblclick', openLink)

  return L.layerGroup([marker, hitTarget])
}

function flanneryRadius(wells: number, minWells = 20, maxWells = 64, minRadius = 12, maxRadius = 40): number {
  const clamped = Math.max(minWells, Math.min(maxWells, wells))
  const t = (clamped - minWells) / (maxWells - minWells)
  return minRadius + (maxRadius - minRadius) * Math.sqrt(t)
}

function formatDate(val: string | null | undefined, format = "MMM D 'YY"): string | null {
  if (!val) return null
  const m = moment(val)
  return m.isValid() ? m.format(format) : null
}

export function formatIdentifier(
  type: 'apiNumber' | 'spillDocNumber' | 'relatedFacilities',
  value: string | null,
): string {
  if (!value) return ''
  let url: string, displayValue: string = value, title: string
  switch (type) {
    case 'apiNumber': {
      const cleaned = value.toString().padStart(10, '0')
      url = `${ECMC_BASE}/Facility/FacilityDetail?api=${cleaned.slice(2)}`
      displayValue = `${cleaned.slice(0, 2)}-${cleaned.slice(2, 5)}-${cleaned.slice(5)}`
      title = 'View facility details at the ECMC.'
      break
    }
    case 'spillDocNumber':
      url = `${ECMC_BASE}/Incidents/reportDetail?report=spill&doc_num=${value}`
      displayValue = '<i class="fa fa-up-right-from-square"></i>'
      title = 'View spill document details at the ECMC.'
      break
    case 'relatedFacilities':
      url = `${ECMC_BASE}/Facility/FacilityDetail?fac=${value}`
      title = 'View related facility details at the ECMC.'
      break
    default:
      throw new Error(`Unsupported identifier type: ${type}`)
  }
  return `<a target="_blank" title="${title}" href="${url}">${displayValue}</a>`
}

// ---------------------------------------------------------------------------
// Help Modal
// ---------------------------------------------------------------------------

export function createHelpModal(map: L.Map): L.Control.EasyButton {
  const STORAGE_KEY = 'river_map_help_seen'

  const content = `
    <div style="max-width:320px;font-size:13px;line-height:1.5">
      <img src="${import.meta.env.BASE_URL}/logo.png" alt="EcoCarto Logo" style="display:block;margin:0 auto 10px;width:280px;height:auto">
      <h3 style="margin:0 0 8px;text-align:center">Poudre River Trail Map: Instructions</h3>
      <p>The Poudre River Trail stretches over 40 miles between Fort Collins and Greeley along the Cache la Poudre River as it flows eastward towards the Platte River. This map shows the hundreds of oil and gas wells, spills, and tank batteries in the region.</p>
      <ul style="margin:12px 0 12px;padding-left:18px">
        <li style="margin-bottom:6px"><b>Double-click</b> a well, spill, or tank battery to view its summary page at the Colorado Energy & Carbon Management Commission (ECMC).</li>
        <li style="margin-bottom:6px">Use the <span class="fa fa-solid fa-location-crosshairs"></span> <b>Locate</b> button to center the map on your current location.</li>
        <li style="margin-bottom:6px">Use the <img src="${import.meta.env.BASE_URL}/map.svg" alt="Layer Control" style="width:18px;height:18px;margin:0 0 -5px 0;display:inline-block;"> <b>Layer Control</b> to toggle the display of features and change the base map.</li>
        <li>Use the <span class="fa fa-regular fa-circle-question"></span> <b>Help</b> button to return to this dialog anytime.</li>
      </ul>
      <button id="help-modal-close" style="
        display:block;margin:0 auto 16px;padding:6px 20px;
        background:#1a80bb;color:#fff;border:none;
        border-radius:4px;cursor:pointer;font-size:13px
      ">Got it!</button>
      <hr style="border:none;border-top:1px solid #ddd;margin:0 0 12px">
      <p style="margin:0 0 8px;font-size:11px;color:#555">
        Visit us at <a href="https://ecocarto.com" target="_blank">ecocarto.com</a> to contract our services,
        and follow <b>@ecocarto</b> on
        <a href="https://www.facebook.com/ecocarto" target="_blank">Facebook</a>,
        <a href="https://www.instagram.com/ecocarto" target="_blank">Instagram</a>, and
        <a href="https://www.threads.net/@ecocarto" target="_blank">Threads</a> for updates.
      </p>
      <p style="margin:0;font-size:11px;color:#555">
        Copyright &copy; 2026 EcoCarto, LLC. All rights reserved, unauthorized reproduction
        and distribution strictly prohibited.
      </p>
    </div>
  `

  const modal = L.popup({
    closeButton: false,
    closeOnClick: false,
    closeOnEscapeKey: true,
    className: 'help-modal',
    maxWidth: 340,
    offset: L.point(0, Math.round(window.innerHeight / 4)),
  })
    .setLatLng(map.getCenter())
    .setContent(content)

  const open = () => {
    modal.setLatLng(map.getCenter()).openOn(map)
    // Use timeout to ensure DOM is ready before binding the close button
    setTimeout(() => {
      document.getElementById('help-modal-close')?.addEventListener('click', () => {
        map.closePopup(modal)
      })
    }, 0)
  }

  // Show on first load only
  if (!sessionStorage.getItem(STORAGE_KEY)) {
    sessionStorage.setItem(STORAGE_KEY, '1')
    open()
  }
  const button = L.easyButton('fa-regular fa-circle-question', () => open()) as L.Control.EasyButton
  return button
}